/**
 * OnboardingWizard — role-tailored first-time walkthrough.
 *
 * Fires automatically on first login (tracked via localStorage per user ID).
 * Can be re-opened from the sidebar "Getting Started" button.
 *
 * Usage:
 *   const { open, dismiss, reopen } = useOnboarding(user);
 *   <OnboardingWizard user={user} open={open} onClose={dismiss} />
 */
import React, { useEffect, useState } from "react";
import {
  ArrowRight,
  CheckCircle2,
  DatabaseBackup,
  FileBarChart,
  LayoutDashboard,
  ListOrdered,
  ShieldCheck,
  UserCog,
  Users,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";

// ── Storage helpers ────────────────────────────────────────────────────────
const storageKey = (userId) => `onboarding_v1_${userId}`;

export const isOnboardingComplete = (userId) => {
  try { return !!localStorage.getItem(storageKey(userId)); } catch { return false; }
};

export const markOnboardingComplete = (userId) => {
  try { localStorage.setItem(storageKey(userId), "1"); } catch {}
};

// ── Step definitions per role ──────────────────────────────────────────────
const COORDINATOR_STEPS = [
  {
    Icon: Users,
    color: "text-blue-600",
    bg: "bg-blue-50",
    title: "Welcome to Transplant Ops Hub",
    body: "You're signed in as a Coordinator. You can add and edit patients, track authorisations, manage documents and action items, and generate reports.",
  },
  {
    Icon: Users,
    color: "text-violet-600",
    bg: "bg-violet-50",
    title: "The Patient Masterlist",
    body: "The Patients page is your main workspace. Search or filter by diagnosis, doctor, status, or pathway. Click any patient to open their full clinical record.",
    cta: { label: "Open Patients", to: "/patients" },
  },
  {
    Icon: LayoutDashboard,
    color: "text-emerald-600",
    bg: "bg-emerald-50",
    title: "Inside a patient record",
    body: "Each record has three tabs — Overview (the full clinical workflow), Documents (PDF uploads), and Action Items. All fields auto-save as you type — there is no Save button.",
  },
  {
    Icon: ShieldCheck,
    color: "text-rose-600",
    bg: "bg-rose-50",
    title: "Auth Alerts — your daily check",
    body: "The red badge on Auth Alerts in the sidebar shows how many authorisations are expiring or already expired. Check this first each morning. Renewing early prevents treatment delays.",
    cta: { label: "Open Auth Alerts", to: "/authorisations" },
  },
  {
    Icon: LayoutDashboard,
    color: "text-brand",
    bg: "bg-brand/8",
    title: "The Dashboard",
    body: "Your personalised worklist lives on the Dashboard — it shows every open Action Item assigned to you across all patients. The KPI cards give a live programme overview.",
    cta: { label: "Go to Dashboard", to: "/" },
  },
];

const ADMIN_STEPS = [
  {
    Icon: UserCog,
    color: "text-brand",
    bg: "bg-brand/8",
    title: "Welcome, Administrator",
    body: "You have full access — patient records, user management, lookup lists, imports, and backups. Start by configuring the system before coordinators begin data entry.",
  },
  {
    Icon: UserCog,
    color: "text-blue-600",
    bg: "bg-blue-50",
    title: "Step 1 — Add your users",
    body: "Go to Admin → Users to create accounts for coordinators. Roles matter: Coordinators can enter and edit patient data; Readonly users can only view records.",
    cta: { label: "Manage Users", to: "/admin/users" },
  },
  {
    Icon: ListOrdered,
    color: "text-violet-600",
    bg: "bg-violet-50",
    title: "Step 2 — Populate Doctors & Lists",
    body: "Before coordinators add patients, populate Admin → Doctors and Admin → Lists. These feed the dropdowns throughout the system — diagnosis names, funding types, patient statuses, and more.",
    cta: { label: "Open Lists", to: "/admin/lists" },
  },
  {
    Icon: DatabaseBackup,
    color: "text-emerald-600",
    bg: "bg-emerald-50",
    title: "Step 3 — Establish a backup routine",
    body: "Admin → Backups lets you download a full database snapshot. Run a backup before every deployment and after bulk imports. Recommend keeping copies off-site.",
    cta: { label: "Open Backups", to: "/admin/backups" },
  },
];

const READONLY_STEPS = [
  {
    Icon: LayoutDashboard,
    color: "text-brand",
    bg: "bg-brand/8",
    title: "Welcome — read-only access",
    body: "You can view all patient records, reports, and programme data, but cannot make changes. If you need editing access, ask your administrator to update your role.",
  },
  {
    Icon: FileBarChart,
    color: "text-blue-600",
    bg: "bg-blue-50",
    title: "Your key pages",
    body: "The Dashboard shows live KPI cards and programme activity. Reports lets you download the bi-weekly meeting agenda, 6-month summary, and yearly report as PDFs.",
    cta: { label: "Open Reports", to: "/reports" },
  },
];

const STEPS_BY_ROLE = {
  coordinator: COORDINATOR_STEPS,
  admin: ADMIN_STEPS,
  readonly: READONLY_STEPS,
};

// ── Hook ───────────────────────────────────────────────────────────────────
export function useOnboarding(user) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (!isOnboardingComplete(user.id)) {
      // Small delay so the rest of the UI renders first.
      const t = setTimeout(() => setOpen(true), 600);
      return () => clearTimeout(t);
    }
  }, [user]);

  const dismiss = () => {
    if (user) markOnboardingComplete(user.id);
    setOpen(false);
  };

  const reopen = () => setOpen(true);

  return { open, dismiss, reopen };
}

// ── Wizard component ────────────────────────────────────────────────────────
export default function OnboardingWizard({ user, open, onClose }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const steps = STEPS_BY_ROLE[user?.role] ?? COORDINATOR_STEPS;
  const total = steps.length;
  const current = steps[step] ?? steps[0];
  const { Icon } = current;
  const isLast = step === total - 1;

  // Reset to first step each time the wizard opens.
  useEffect(() => {
    if (open) setStep(0);
  }, [open]);

  const handleNext = () => {
    if (isLast) {
      onClose();
    } else {
      setStep((s) => s + 1);
    }
  };

  const handleCta = (to) => {
    onClose();
    navigate(to);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-md p-0 gap-0 overflow-hidden"
        data-testid="onboarding-wizard"
      >
        {/* Step progress bar */}
        <div className="flex gap-1.5 px-6 pt-5">
          {steps.map((_, i) => (
            <div
              key={i}
              className={`h-1 flex-1 rounded-full transition-colors duration-300 ${
                i <= step ? "bg-brand" : "bg-zinc-200"
              }`}
            />
          ))}
        </div>

        {/* Main content */}
        <div className="px-6 pt-5 pb-4">
          <div
            className={`inline-flex items-center justify-center w-12 h-12 rounded-xl mb-4 ${current.bg}`}
          >
            <Icon className={`w-6 h-6 ${current.color}`} strokeWidth={1.5} />
          </div>
          <h2
            className="font-heading text-xl font-semibold text-zinc-900 dark:text-zinc-100 leading-snug mb-2"
            data-testid="onboarding-title"
          >
            {current.title}
          </h2>
          <p className="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed">
            {current.body}
          </p>
        </div>

        {/* Footer */}
        <div className="px-6 pb-5 flex items-center justify-between gap-3 border-t border-zinc-100 dark:border-zinc-800 pt-4">
          <span className="text-xs text-zinc-400 tabular-nums">
            {step + 1} of {total}
          </span>
          <div className="flex items-center gap-2">
            {current.cta && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleCta(current.cta.to)}
                data-testid="onboarding-cta"
              >
                {current.cta.label}
              </Button>
            )}
            <Button
              size="sm"
              className="bg-brand hover:bg-brand-dark text-white"
              onClick={handleNext}
              data-testid="onboarding-next"
            >
              {isLast ? (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-1.5" />
                  Done
                </>
              ) : (
                <>
                  Next
                  <ArrowRight className="w-4 h-4 ml-1.5" />
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
